import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, mergeMap, switchMap, withLatestFrom, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { BaseCoreState } from '../reducers';
import { baseAuthSelectors } from '../selectors';
import { baseInquiryActions, baseOffersAndBookingsActions } from '../actions';
import { BaseOffersAndBookingsService } from '../../modules/offers-and-bookings/base-offers-and-bookings.service';
import { BaseTokenInterface } from '../../shared/interfaces/base-token-service.interface';
import { BaseListPagination } from '../../shared/enums/base-list-pagination.enum';
import { BaseOffersAndBookingsListPayloadInterface } from '../../modules/offers-and-bookings/_interfaces/base-offers-and-bookings-list-payload.interface';
import { includeIf } from '../../shared/helpers/base-include-action';
import { getLastChildRoute } from '../../shared/helpers/base-get-last-route-child';

@Injectable()
export class BaseOffersAndBookingsEffects {
  constructor(
    private actions$: Actions,
    private store: Store<BaseCoreState>,
    private offersAndBookingsService: BaseOffersAndBookingsService,
    private activatedRoute: ActivatedRoute
  ) {}

  loadOfferOverlapsList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseOffersAndBookingsActions.loadOfferOverlapsList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.offersAndBookingsService
          .getPspOfferOverlapList({
            ...action.payload,
            psp: user.organizationId,
            page: action.payload.page || BaseListPagination.page,
            size: action.payload.size || BaseListPagination.size,
          })
          .pipe(
            mergeMap((response) => [
              baseOffersAndBookingsActions.setOfferOverlapsList({ payload: response }),
              baseOffersAndBookingsActions.successOfferOverlapsList(),
            ]),
            catchError(() => of(baseOffersAndBookingsActions.errorOfferOverlapsList()))
          );
      })
    );
  });

  confirmPspInquiryOffer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseOffersAndBookingsActions.confirmPspInquiryOffer),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.offersAndBookingsService
          .confirmPspInquiryOffer({
            psp: user.organizationId,
            inquiry: action.payload.inquiry,
            offer: action.payload.offer,
            body: action.payload.body,
          })
          .pipe(
            mergeMap((response) => {
              const queryParams = this.activatedRoute.snapshot.queryParams;
              let route = getLastChildRoute(this.activatedRoute);

              return action.payload.isInquiryDetailsScreen
                ? [
                    baseOffersAndBookingsActions.confirmPspInquiryOfferSuccess(),
                    ...includeIf(
                      route.snapshot.data['isEmployees'],
                      baseInquiryActions.loadInquiryCandidatesList({
                        payload: { inquiry: action.payload.inquiry, ...queryParams },
                      })
                    ),
                    ...includeIf(
                      route.snapshot.data['isOffers'],
                      baseInquiryActions.loadPspInquiryOffersList({
                        payload: { inquiry: action.payload.inquiry, ...queryParams },
                      })
                    ),
                    baseInquiryActions.loadPSPInquiry({ payload: action.payload.inquiry }),
                  ]
                : [
                    baseOffersAndBookingsActions.confirmPspInquiryOfferSuccess(),
                    baseOffersAndBookingsActions.loadOfferOverlapsList({
                      payload: queryParams as BaseOffersAndBookingsListPayloadInterface,
                    }),
                  ];
            }),
            catchError(() => of(baseOffersAndBookingsActions.confirmPspInquiryOfferError()))
          );
      })
    );
  });

  withdrawPspInquiryOffer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseOffersAndBookingsActions.withdrawPspInquiryOffer),
      withLatestFrom(this.store.select(baseAuthSelectors.getUserInfo)),
      switchMap(([action, user]) => {
        return this.offersAndBookingsService
          .withdrawPspInquiryOffer({
            psp: user.data?.organizationId as number,
            inquiry: action.payload.inquiry,
            offer: action.payload.offer,
          })
          .pipe(
            mergeMap(() => {
              const queryParams = this.activatedRoute.snapshot.queryParams;

              return action.payload.isInquiryOffersPage
                ? [
                    baseOffersAndBookingsActions.withdrawPspInquiryOfferSuccess(),
                    baseInquiryActions.loadPspInquiryOffersList({
                      payload: { ...queryParams, inquiry: action.payload.inquiry },
                    }),
                    baseInquiryActions.loadInquiryCandidatesList({
                      payload: { ...queryParams, inquiry: action.payload.inquiry },
                    }),
                    baseInquiryActions.loadPSPInquiry({ payload: action.payload.inquiry }),
                  ]
                : [
                    baseOffersAndBookingsActions.withdrawPspInquiryOfferSuccess(),
                    baseOffersAndBookingsActions.loadOfferOverlapsList({
                      payload: queryParams as BaseOffersAndBookingsListPayloadInterface,
                    }),
                  ];
            }),
            catchError(() => of(baseOffersAndBookingsActions.withdrawPspInquiryOfferError()))
          );
      })
    );
  });
}
